export default class OmeReceiver {
	constructor(url, { onStream, onClose } = {}) {
		this._url = url;
		this._onStream = onStream;
		this._onClose = onClose;
		this._ws = null;
		this._pc = null;
		this._id = null;
		this._peerId = null;
		this._stopped = false;
	}

	start() {
		this._stopped = false;
		this._connect();
	}

	stop() {
		this._stopped = true;
		this._cleanup();
	}

	_connect() {
		let ws;
		try {
			ws = new WebSocket(this._url);
		} catch(e) {
			return;
		}
		this._ws = ws;

		ws.onopen = () => {
			ws.send(JSON.stringify({ command: 'request_offer' }));
		};

		ws.onmessage = (e) => {
			let msg;
			try { msg = JSON.parse(e.data); } catch(_) { return; }
			if (msg.command === 'offer') {
				this._handleOffer(msg);
			} else if (msg.command === 'candidate' && this._pc && msg.candidates) {
				msg.candidates.forEach(c => {
					this._pc.addIceCandidate(new RTCIceCandidate(c)).catch(() => {});
				});
			} else if (msg.command !== 'notification') {
				// Unrecognized command (e.g. OME 404 "Cannot create offer") — close and let retry handle it
				this._cleanup();
				if (!this._stopped && this._onClose) this._onClose();
			}
		};

		ws.onerror = () => {};

		ws.onclose = () => {
			this._cleanup();
			if (!this._stopped && this._onClose) this._onClose();
		};
	}

	async _handleOffer(msg) {
		this._id = msg.id;
		this._peerId = msg.peer_id;

		const pc = new RTCPeerConnection({ iceServers: [] });
		this._pc = pc;

		pc.ontrack = (e) => {
			// Minimize jitter buffer / playout delay for lowest latency
			if ('playoutDelayHint' in e.receiver) e.receiver.playoutDelayHint = 0;
			if ('jitterBufferTarget' in e.receiver) e.receiver.jitterBufferTarget = 0;
			if (this._onStream) this._onStream(e.streams[0] || new MediaStream([e.track]));
		};

		pc.onicecandidate = (e) => {
			if (e.candidate && e.candidate.candidate && this._ws && this._ws.readyState === WebSocket.OPEN) {
				this._ws.send(JSON.stringify({
					command: 'candidate',
					id: this._id,
					peer_id: this._peerId,
					candidates: [e.candidate],
				}));
			}
		};

		try {
			await pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));

			// Apply latency hints immediately — receivers exist after setRemoteDescription.
			// ontrack also applies these but fires later, after the jitter buffer has already sized itself.
			pc.getReceivers().forEach(receiver => {
				if ('playoutDelayHint' in receiver) receiver.playoutDelayHint = 0;
				if ('jitterBufferTarget' in receiver) receiver.jitterBufferTarget = 0;
			});

			// Must add candidates after setRemoteDescription, otherwise they are silently dropped
			if (msg.candidates) {
				msg.candidates.forEach(c => {
					pc.addIceCandidate(new RTCIceCandidate(c)).catch(() => {});
				});
			}

			const answer = await pc.createAnswer();
			await pc.setLocalDescription(answer);
			if (this._ws && this._ws.readyState === WebSocket.OPEN) {
				this._ws.send(JSON.stringify({
					command: 'answer',
					id: this._id,
					peer_id: this._peerId,
					sdp: answer,
				}));
			}
		} catch(e) {
			this._cleanup();
			if (!this._stopped && this._onClose) this._onClose();
		}
	}

	_cleanup() {
		if (this._pc) { this._pc.close(); this._pc = null; }
		if (this._ws) {
			this._ws.onclose = null;
			this._ws.onerror = null;
			this._ws.close();
			this._ws = null;
		}
	}
}
